import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Container,
  Flex,
  Heading,
  Text,
  Card,
  Button,
  Callout,
  Separator,
  Box,
  RadioGroup,
  Badge,
  Select,
  Dialog,
  TextField,
} from '@radix-ui/themes'
import { ArrowLeft, Download, XCircle, Loader2, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

import { TransferStatusBadge } from '../components/TransferStatusBadge'
import { TransferStatusTimeline } from '../components/TransferStatusTimeline'
import { TransferDocumentUpload } from '../components/TransferDocumentUpload'
import { TransferPaymentButton } from '../components/TransferPaymentButton'

import { Transfer, TransferStatus, TransferType } from '@/interfaces/Transfer'
import useTransferFetcher from '@/hooks/useTransferFetcher'
import { AppPath } from '@/enums/AppPath'
import { customPost } from '@/utils/customFetch'
import { makeUrlTransferVoid, makeUrlTransfer } from '@/hooks/connectionData'
import { useAuth } from '@/hooks/useAuth'
import useTransferUpdates from '@/hooks/useTransferUpdates'
import { getToken } from '@/utils/utils'

const formatCLP = (amount: number): string => {
  return `$${amount.toLocaleString('es-CL')}`
}

export const TransferDetailScreen = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { userData } = useAuth()
  const { fetchTransfer, loading, errorTransfer } = useTransferFetcher()
  const organizationId = userData?.organization?._id

  const [transfer, setTransfer] = useState<Transfer | null>(null)
  const [voidCause, setVoidCause] = useState('')
  const [updatingType, setUpdatingType] = useState(false)
  const [showVoidDialog, setShowVoidDialog] = useState(false)
  const [voiding, setVoiding] = useState(false)
  const [showAppraisalDialog, setShowAppraisalDialog] = useState(false)
  const [newAppraisal, setNewAppraisal] = useState('')

  // SSE real-time updates
  const { latestUpdates } = useTransferUpdates(organizationId)

  const loadTransfer = async () => {
    if (!id) return
    const data = await fetchTransfer(id)

    if (data) setTransfer(data)
  }

  const handleTypeChange = async (newType: TransferType) => {
    if (!transfer || !organizationId || updatingType) return

    // Optimistic update: change local state immediately
    const previousTransfer = { ...transfer }
    setTransfer({ ...transfer, type: newType })

    setUpdatingType(true)
    try {
      const url = `${makeUrlTransfer(transfer._id)}/type`
      const response = await axios.patch(
        url,
        {
          type: newType,
          organizationId,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      )

      // Use the updated transfer returned directly by the server
      if (response.data && response.data.transfer) {
        setTransfer(response.data.transfer)
      }
    } catch (err: any) {
      // Revert if it fails
      setTransfer(previousTransfer)
      alert(err.response?.data?.error || err.message || 'Error al actualizar tipo de transferencia')
    } finally {
      setUpdatingType(false)
    }
  }

  useEffect(() => {
    loadTransfer()
  }, [id])

  // Refresh when SSE updates affect this transfer
  useEffect(() => {
    if (latestUpdates.some((u) => u._id === id)) {
      loadTransfer()
    }
  }, [latestUpdates])

  const handleUpdateCalculationBase = async (baseVal: string) => {
    if (!transfer || !organizationId) return

    setUpdatingType(true)
    try {
      const url = `${makeUrlTransfer(transfer._id)}/type`
      const response = await axios.patch(
        url,
        {
          taxCalculationBase: baseVal,
          organizationId,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      )
      if (response.data && response.data.transfer) {
        setTransfer(response.data.transfer)
      }
    } catch (err: any) {
      alert(err.response?.data?.error || err.message || 'Error al actualizar base de cálculo')
    } finally {
      setUpdatingType(false)
    }
  }

  const handleUpdateAppraisal = async () => {
    if (!transfer || !organizationId || !newAppraisal) return
    const val = Number(newAppraisal)

    if (isNaN(val) || val < 0) return

    setUpdatingType(true) // Reuse existing loading state
    try {
      const url = `${makeUrlTransfer(transfer._id)}/type`
      const response = await axios.patch(
        url,
        {
          fiscalAppraisal: val,
          organizationId,
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      )

      if (response.data && response.data.transfer) {
        setTransfer(response.data.transfer)
      }
      setShowAppraisalDialog(false)
    } catch (err: any) {
      alert(err.response?.data?.error || err.message || 'Error al actualizar tasación')
    } finally {
      setUpdatingType(false)
    }
  }

  const handleVoid = async () => {
    if (!transfer || !organizationId || !voidCause) return
    setVoiding(true)
    try {
      await customPost(makeUrlTransferVoid(), {
        transferId: transfer._id,
        cause: voidCause,
        organizationId,
      })
      setShowVoidDialog(false)
      loadTransfer()
    } catch (err: any) {
      alert(err.message || 'Error al anular transferencia')
    } finally {
      setVoiding(false)
    }
  }

  // Check if all required documents are uploaded
  const hasRequiredDocs =
    transfer?.documents?.frontBuyerId &&
    transfer?.documents?.backBuyerId &&
    transfer?.documents?.frontSellerId &&
    transfer?.documents?.backSellerId &&
    transfer?.documents?.draftContract &&
    transfer?.documents?.circulationPermit &&
    ((transfer?.seller?.type !== 'Legal' && transfer?.buyer?.type !== 'Legal') || transfer?.documents?.powerDocument) &&
    (transfer?.seller?.type !== 'Legal' || transfer?.documents?.backSellerRepId)

  if (loading && !transfer) {
    return (
      <Container p="4">
        <Text>Cargando...</Text>
      </Container>
    )
  }

  if (errorTransfer || !transfer) {
    return (
      <Container p="4">
        <Callout.Root color="red">
          <Callout.Text>{errorTransfer?.message || 'Transferencia no encontrada'}</Callout.Text>
        </Callout.Root>
      </Container>
    )
  }

  const canVoid =
    transfer.status !== TransferStatus.NOTARIZED &&
    transfer.status !== TransferStatus.REGISTERED &&
    transfer.status !== TransferStatus.VOIDED

  return (
    <Flex direction="column" gap="4" p="4" width="100%" overflow="auto">
      {/* Header */}
      <Flex align="center" gap="3">
        <Button variant="ghost" onClick={() => navigate(`/${AppPath.Transfers}`)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <Heading size="5">Transferencia #{transfer.number}</Heading>
        <TransferStatusBadge status={transfer.status} />
      </Flex>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left column */}
        <Flex direction="column" gap="4">
          {/* Vehicle info */}
          <Card>
            <Flex direction="column" gap="2" p="3">
              <Text size="3" weight="bold">
                Vehículo
              </Text>
              <Text size="2">
                Patente: <strong>{transfer.vehicleData.plate}</strong>
              </Text>
              <Text size="2">
                {transfer.vehicleData.brand} {transfer.vehicleData.model} {transfer.vehicleData.year}
              </Text>
              {transfer.vehicleData.vin && (
                <Text size="1" color="gray">
                  VIN: {transfer.vehicleData.vin}
                </Text>
              )}
              {transfer.vehicleData.chassis && (
                <Text size="1" color="gray">
                  Chasis: {transfer.vehicleData.chassis}
                </Text>
              )}
              {transfer.vehicleData.color && (
                <Text size="1" color="gray">
                  Color: {transfer.vehicleData.color}
                </Text>
              )}
            </Flex>
          </Card>

          {/* Buyer/Seller */}
          <Card>
            <Flex direction="column" gap="3" p="3">
              <Text size="3" weight="bold">
                Comprador
              </Text>
              <Text size="2">
                {transfer.buyer.name}{transfer.buyer.rut ? ` (${transfer.buyer.rut})` : ''}
              </Text>
              {transfer.compraPara?.enabled && (
                <Box className="mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                  <Text size="1" weight="bold" className="block mb-1">
                    Compra para tercero:
                  </Text>
                  <Text size="2">
                    {transfer.compraPara.ownerName}{transfer.compraPara.ownerRut ? ` - ${transfer.compraPara.ownerRut}` : ''}
                  </Text>
                </Box>
              )}

              <Separator />

              <Text size="3" weight="bold">
                Vendedor
              </Text>
              <Text size="2">
                {transfer.seller.name}{transfer.seller.rut ? ` (${transfer.seller.rut})` : ''}
              </Text>

              {/* Legal Entity Details */}
              {transfer.seller.type === 'Legal' && transfer.seller.legalEntity && (
                <Box className="mt-2 bg-gray-50 border border-gray-200 rounded p-2 text-sm">
                  <Text size="1" weight="bold" color="gray" className="block mb-1">
                    Representante Legal:
                  </Text>
                  <Text size="2" className="block mb-2">
                    {transfer.seller.legalEntity.repName} ({transfer.seller.legalEntity.repRut})
                  </Text>

                  <Text size="1" weight="bold" color="gray" className="block mb-1">
                    Datos Escritura:
                  </Text>
                  <Text size="1" className="block">
                    Notaría: {transfer.seller.legalEntity.notaryOffice || 'No definida'}
                  </Text>
                  <Text size="1" className="block">
                    Fecha: {transfer.seller.legalEntity.powerDate || 'No definida'}
                  </Text>

                  {(!transfer.seller.legalEntity.notaryOffice || !transfer.seller.legalEntity.powerDate) && (
                    <Text size="1" color="red" className="mt-1 block font-bold">
                      ⚠ Faltan datos legales. Configure la Organización.
                    </Text>
                  )}
                </Box>
              )}
            </Flex>
          </Card>

          {/* Transfer Type Selection */}
          <Card>
            <Flex direction="column" gap="3" p="3">
              <Flex justify="between" align="center">
                <Text size="3" weight="bold">
                  Plan de servicio
                </Text>
                {transfer.type === TransferType.EXPRESS && (
                  <Badge color="teal" variant="soft">
                    <Zap className="w-3 h-3 mr-1" />
                    Express
                  </Badge>
                )}
                {transfer.type === TransferType.STANDARD && (
                  <Badge color="gray" variant="soft">
                    Estándar
                  </Badge>
                )}
              </Flex>

              {[TransferStatus.PENDING_DOCUMENTS, TransferStatus.PENDING_PAYMENT, TransferStatus.DRAFT].includes(
                transfer.status
              ) ? (
                <Box>
                  <RadioGroup.Root
                    value={transfer.type || TransferType.STANDARD}
                    onValueChange={(val) => handleTypeChange(val as TransferType)}
                  >
                    <Flex gap="4">
                      <RadioGroup.Item value={TransferType.STANDARD}>
                        <Text size="2" weight={transfer.type === TransferType.STANDARD ? 'bold' : 'regular'}>
                          Estándar
                        </Text>
                      </RadioGroup.Item>
                      <RadioGroup.Item value={TransferType.EXPRESS}>
                        <Text
                          size="2"
                          color="teal"
                          weight={transfer.type === TransferType.EXPRESS ? 'bold' : 'regular'}
                        >
                          Express
                        </Text>
                      </RadioGroup.Item>
                    </Flex>
                  </RadioGroup.Root>
                  {updatingType && (
                    <Flex align="center" gap="2" mt="2">
                      <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                      <Text size="1" color="gray" className="italic">
                        Actualizando plan...
                      </Text>
                    </Flex>
                  )}
                </Box>
              ) : (
                <Text size="2" color="gray">
                  {transfer.type === TransferType.EXPRESS
                    ? 'Servicio Express contratado'
                    : 'Servicio Estándar contratado'}
                </Text>
              )}
            </Flex>
          </Card>

          {/* Financial */}
          <Card>
            <Flex direction="column" gap="2" p="3">
              <Text size="3" weight="bold">
                Costos
              </Text>
              <Flex justify="between">
                <Text size="2">Precio venta</Text>
                <Text size="2">{formatCLP(transfer.price)}</Text>
              </Flex>

              <Flex justify="between">
                <Text size="2">Tasación fiscal</Text>
                <Flex gap="2" align="center">
                  <Text size="2">{formatCLP(transfer.fiscalAppraisal)}</Text>
                  {['draft', 'pending_documents', 'pending_payment'].includes(transfer.status) && (
                    <Button
                      variant="ghost"
                      size="1"
                      onClick={() => {
                        setNewAppraisal(transfer.fiscalAppraisal.toString())
                        setShowAppraisalDialog(true)
                      }}
                    >
                      Editar
                    </Button>
                  )}
                </Flex>
              </Flex>

              <Separator />

              <Flex direction="column" gap="1">
                <Flex justify="between">
                  <Text size="2">Impuesto municipal (1.5%)</Text>
                  <Text size="2">{formatCLP(transfer.municipalTax)}</Text>
                </Flex>
                <Flex justify="between" align="center">
                  <Text size="1" color="gray">
                    Base del cálculo:
                  </Text>
                  <Select.Root
                    value={transfer.taxCalculationBase || 'highest'}
                    onValueChange={(val: string) => handleUpdateCalculationBase(val)}
                    disabled={
                      !['draft', 'pending_documents', 'pending_payment'].includes(transfer.status) || updatingType
                    }
                  >
                    <Select.Trigger variant="ghost" color="gray" />
                    <Select.Content>
                      <Select.Item value="highest">Mayor valor (Legal)</Select.Item>
                      <Select.Item value="price">Precio Venta</Select.Item>
                      <Select.Item value="appraisal">Tasación</Select.Item>
                    </Select.Content>
                  </Select.Root>
                </Flex>
              </Flex>

              <Flex justify="between">
                <Text size="2">Arancel Registro Civil</Text>
                <Text size="2">{formatCLP(transfer.civilRegistryFee)}</Text>
              </Flex>
              <Flex justify="between">
                <Text size="2">Servicio transferencia</Text>
                <Text size="2">{formatCLP(transfer.transfieroFee + transfer.veeklsCommission)}</Text>
              </Flex>
              <Flex justify="between">
                <Text size="2">Comisión Mercado Pago</Text>
                <Text size="2">{formatCLP(transfer.mercadoPagoFee || 0)}</Text>
              </Flex>
              <Separator />
              <Flex justify="between">
                <Text size="2" weight="bold">
                  Total
                </Text>
                <Text size="2" weight="bold" color="blue">
                  {formatCLP(transfer.totalCost)}
                </Text>
              </Flex>
            </Flex>
          </Card>

          {/* Payment & processing section */}
          {(transfer.status === TransferStatus.PENDING_DOCUMENTS ||
            transfer.status === TransferStatus.PENDING_PAYMENT) && (
            <Card>
              <Flex direction="column" gap="2" p="3">
                <Text size="2" weight="bold">
                  Pago del servicio
                </Text>
                {hasRequiredDocs ? (
                  <>
                    <Text size="2" color="gray">
                      Los documentos requeridos están listos. Realice el pago para que la transferencia sea enviada
                      automáticamente a Transfiero Online.
                    </Text>
                    <TransferPaymentButton
                      transferId={transfer._id}
                      initPoint={
                        transfer.mercadoPagoPreferenceId
                          ? `https://www.mercadopago.cl/checkout/v1/redirect?pref_id=${transfer.mercadoPagoPreferenceId}`
                          : undefined
                      }
                    />
                  </>
                ) : (
                  <Text size="2" color="amber">
                    Debe subir los documentos requeridos antes de pagar: cédulas frontal y reverso del comprador y
                    vendedor, borrador del contrato (PDF) y el permiso de circulación.
                  </Text>
                )}
              </Flex>
            </Card>
          )}

          {/* Processing animation — shown when payment confirmed, auto-submitting to Transfiero */}
          {transfer.status === TransferStatus.PAID && !transfer.transfieroId && (
            <Card>
              <Flex direction="column" align="center" gap="3" p="5">
                <div className="relative flex items-center justify-center">
                  <div
                    className="absolute w-16 h-16 rounded-full border-4 border-teal-200"
                    style={{ animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}
                  />
                  <Loader2 className="w-10 h-10 text-teal-600" style={{ animation: 'spin 1.5s linear infinite' }} />
                </div>
                <Text size="3" weight="bold" align="center">
                  Enviando a Transfiero Online...
                </Text>
                <Text size="2" color="gray" align="center">
                  El pago fue confirmado. Su transferencia se está procesando automáticamente. Esto puede tardar unos
                  segundos.
                </Text>
                <style>{`
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                  @keyframes pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.3; transform: scale(1.3); }
                  }
                `}</style>
              </Flex>
            </Card>
          )}

          {/* Transfiero info */}
          {transfer.transfieroId && (
            <Card>
              <Flex direction="column" gap="2" p="3">
                <Text size="3" weight="bold">
                  Transfiero Online
                </Text>
                <Text size="2">
                  ID: <strong>{transfer.transfieroId}</strong>
                </Text>

                {/* Buyer Signature Details */}
                {transfer.buyerFirmaCode && (
                  <div className="mt-2 p-3 rounded-md border border-blue-200 bg-blue-50 transition-colors duration-200 hover:bg-blue-100 hover:border-blue-300">
                    <Flex align="center" justify="between">
                      <Flex direction="column" gap="1">
                        <Text size="2" color="blue" weight="bold">
                          Firma Comprador
                        </Text>
                        <Text size="1" color="gray">
                          Código Grup: <strong className="font-mono text-blue-800">{transfer.buyerFirmaCode}</strong>
                        </Text>
                      </Flex>
                      {transfer.buyerFirmaLink && (
                        <Button variant="soft" color="blue" size="2" asChild>
                          <a href={transfer.buyerFirmaLink} target="_blank" rel="noopener noreferrer">
                            Ir a firmar
                          </a>
                        </Button>
                      )}
                    </Flex>
                  </div>
                )}

                {/* Seller Signature Details */}
                {transfer.sellerFirmaCode && (
                  <div className="mt-2 p-3 rounded-md border border-emerald-200 bg-emerald-50 transition-colors duration-200 hover:bg-emerald-100 hover:border-emerald-300">
                    <Flex align="center" justify="between">
                      <Flex direction="column" gap="1">
                        <Text size="2" color="green" weight="bold">
                          Firma Vendedor
                        </Text>
                        <Text size="1" color="gray">
                          Código Grup:{' '}
                          <strong className="font-mono text-emerald-800">{transfer.sellerFirmaCode}</strong>
                        </Text>
                      </Flex>
                      {transfer.sellerFirmaLink && (
                        <Button variant="soft" color="green" size="2" asChild>
                          <a href={transfer.sellerFirmaLink} target="_blank" rel="noopener noreferrer">
                            Ir a firmar
                          </a>
                        </Button>
                      )}
                    </Flex>
                  </div>
                )}

                {transfer.documents?.notarizedContract && (
                  <Button variant="soft" size="2">
                    <Download className="w-4 h-4" />
                    Descargar contrato notariado
                  </Button>
                )}
              </Flex>
            </Card>
          )}
        </Flex>

        {/* Right column */}
        <Flex direction="column" gap="4">
          {/* Status Timeline */}
          <TransferStatusTimeline statusHistory={transfer.statusHistory} />

          {/* Documents */}
          <TransferDocumentUpload
            transferId={transfer._id}
            organizationId={transfer.organization}
            documents={transfer.documents || {}}
            documentAnalysis={transfer.documentAnalysis}
            onUploadComplete={loadTransfer}
            sellerType={transfer.seller?.type}
            disabled={
              transfer.status !== TransferStatus.PENDING_DOCUMENTS &&
              transfer.status !== TransferStatus.PENDING_PAYMENT &&
              transfer.status !== TransferStatus.DRAFT
            }
          />

          {/* Void button */}
          {canVoid && (
            <Card>
              <Flex direction="column" gap="2" p="3">
                {!showVoidDialog ? (
                  <Button variant="soft" color="red" onClick={() => setShowVoidDialog(true)}>
                    <XCircle className="w-4 h-4" />
                    Anular Transferencia
                  </Button>
                ) : (
                  <Flex direction="column" gap="2">
                    <Text size="2" weight="bold" color="red">
                      Anular Transferencia
                    </Text>
                    <textarea
                      className="w-full border rounded-md p-2 text-sm"
                      placeholder="Causa de anulación..."
                      value={voidCause}
                      onChange={(e) => setVoidCause(e.target.value)}
                      rows={3}
                    />
                    <Flex gap="2">
                      <Button variant="soft" color="gray" onClick={() => setShowVoidDialog(false)}>
                        Cancelar
                      </Button>
                      <Button color="red" disabled={!voidCause || voiding} onClick={handleVoid}>
                        {voiding ? 'Anulando...' : 'Confirmar Anulación'}
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </Flex>
            </Card>
          )}

          <Dialog.Root open={showAppraisalDialog} onOpenChange={setShowAppraisalDialog}>
            <Dialog.Content style={{ maxWidth: 450 }}>
              <Dialog.Title>Editar Tasación Fiscal</Dialog.Title>
              <Dialog.Description size="2" mb="4">
                Ingrese el nuevo valor de tasación fiscal. Esto recalculará el impuesto municipal y el costo total de la
                transferencia.
              </Dialog.Description>

              <Flex direction="column" gap="3">
                <label>
                  <Text as="div" size="2" mb="1" weight="bold">
                    Nueva Tasación Fiscal
                  </Text>
                  <TextField.Root
                    placeholder="Ej: 15000000"
                    type="number"
                    value={newAppraisal}
                    onChange={(e) => setNewAppraisal(e.target.value)}
                  />
                </label>
              </Flex>

              <Flex gap="3" mt="4" justify="end">
                <Dialog.Close>
                  <Button variant="soft" color="gray">
                    Cancelar
                  </Button>
                </Dialog.Close>
                <Button onClick={handleUpdateAppraisal} disabled={updatingType || !newAppraisal}>
                  {updatingType ? 'Actualizando...' : 'Actualizar'}
                </Button>
              </Flex>
            </Dialog.Content>
          </Dialog.Root>
        </Flex>
      </div>
    </Flex>
  )
}
